import { useEffect, useState } from "react"

import Steps from "./steps"
import PlatformSelection from "./platform_selection"
import GoalSelection from "./goal_selection"
import ServiceSelection from "./service_selection"
import Upsell from "./upsell"
import RecommendedApplets from "./recommended_applets"
import { Service } from "types"
import { TrialConfigAssets, TrialConfigUrls } from "./upsell"
import { submitPreferredPlatforms } from "./api"

interface Props {
  browserPlatform: string
  connectedServiceObj: Service
  connectingToAssets: {
    desktop: string
    responsive: string
  }
  userIsOnboarding: boolean
  pageId: string | null
  paidUser: boolean
  progress?: string
  trialConfig: {
    assets: TrialConfigAssets
    urls: TrialConfigUrls
  }
  urls: {
    exploreUrl: string
    bulkChannelBaseUrl: string
    returnToUrl: string
  }
  userIsStudent: boolean
}

const getSteps = (step: string, progress?: number): Array<string> => {
  const possibleSteps = ["platforms", "goals", "services", "applets"]

  if (progress && progress === 3) {
    return possibleSteps
  } else if (progress && progress === 2) {
    return possibleSteps.slice(1)
  }

  switch (step) {
    case "goals":
      return possibleSteps.slice(1)
    case "services":
      return possibleSteps.slice(2)
    default:
      return possibleSteps
  }
}

const NewUserIntake = ({
  browserPlatform,
  connectedServiceObj,
  connectingToAssets,
  userIsOnboarding,
  pageId,
  paidUser,
  progress,
  trialConfig,
  urls,
  userIsStudent,
}: Props) => {
  const [selectedService, setSelectedService] = useState<Service | null>(connectedServiceObj)
  const pathName = userIsOnboarding ? "/onboarding" : "/applet_discovery"
  const STEPS = Object.values(Steps)
  if (userIsStudent) {
    Steps.UPSELL = "student_discount"
  }
  const [step, setStep] = useState<string>(
    browserPlatform === "desktop" ? Steps.PLATFORM_SELECTION : Steps.GOAL_SELECTION
  )
  const [stepHistory, setStepHistory] = useState<Array<string>>([])
  const [progressBarSteps, setProgressBarSteps] = useState<Array<string>>([])
  const [selectedGoal, setSelectedGoal] = useState<string>()

  const browserToMobilePlatformMapping: { [key: string]: string } = {
    ios: "iphone",
    android: "android",
  }

  useEffect(() => {
    const userMobilePlatform = browserToMobilePlatformMapping[browserPlatform]

    if (userIsOnboarding && userMobilePlatform) {
      submitPreferredPlatforms([userMobilePlatform])
    }
  }, [browserPlatform, userIsOnboarding])

  useEffect(() => {
    const validStep = pageId ? STEPS.includes(pageId) : false

    if (validStep && pageId) {
      setStep(pageId)
    } else {
      const url = pathName + `/${step}`
      window.history.replaceState({ turbolinks: true }, "", url)
    }
  }, [])

  useEffect(() => {
    setProgressBarSteps(getSteps(step, Number(progress)))
  }, [])

  const navigateToNextStep = (currentStep: string, nextStep: string) => {
    setStep(nextStep)

    const url = pathName + `/${nextStep}`
    window.history.pushState({ turbolinks: true }, "", url)

    if (!stepHistory.includes(currentStep)) {
      setStepHistory([...stepHistory, currentStep])
    }
  }

  const onClickBack = (): void => {
    if (stepHistory.length > 0) {
      const previousStep = stepHistory[stepHistory.length - 1]
      setStep(previousStep)

      const history = stepHistory.slice(0, stepHistory.length - 1)

      setStepHistory(history)
    }
    window.history.back()
  }

  const onPlatformSelected = (): void => {
    navigateToNextStep(Steps.PLATFORM_SELECTION, Steps.GOAL_SELECTION)
  }

  const onGoalSelected = (goal: string) => {
    setSelectedGoal(goal)

    let nextStep = Steps.SERVICE_SELECTION
    if (userIsOnboarding && !paidUser) {
      nextStep = Steps.UPSELL
    } else if (goal === "something_else") {
      nextStep = Steps.RECOMMENDED_APPLETS
    }

    navigateToNextStep(Steps.GOAL_SELECTION, nextStep)
  }

  const onServiceSelected = (service: Service): void => {
    setSelectedService(service)
    if (service.connected) navigateToNextStep(Steps.SERVICE_SELECTION, Steps.RECOMMENDED_APPLETS)
  }

  const onSkipUpsell = () => {
    const nextStep = selectedGoal === "something_else" ? Steps.RECOMMENDED_APPLETS : Steps.SERVICE_SELECTION
    navigateToNextStep(Steps.UPSELL, nextStep)
  }

  const onSkipServiceSelection = () => {
    setSelectedService(null)
    navigateToNextStep(Steps.SERVICE_SELECTION, Steps.RECOMMENDED_APPLETS)
  }

  const onSkipRecommendedApplets = () => {
    sessionStorage.removeItem("userOnboardingBucket")
    window.location.assign("/my_applets")
  }

  switch (step) {
    case Steps.PLATFORM_SELECTION:
      return (
        <PlatformSelection
          browserPlatform={browserPlatform}
          onClickBack={onClickBack}
          onPlatformSelected={onPlatformSelected}
          progressBarSteps={progressBarSteps}
          userIsOnboarding={userIsOnboarding}
        />
      )
    case Steps.GOAL_SELECTION:
      return (
        <GoalSelection
          browserPlatform={browserPlatform}
          onClickBack={onClickBack}
          onGoalSelected={onGoalSelected}
          progressBarSteps={progressBarSteps}
          userIsOnboarding={userIsOnboarding}
        />
      )
    case Steps.UPSELL:
      return (
        <Upsell
          onClickBack={onClickBack}
          onSkip={onSkipUpsell}
          trialConfig={trialConfig}
          userIsStudent={userIsStudent}
        />
      )
    case Steps.SERVICE_SELECTION:
      return (
        <ServiceSelection
          connectingToAssets={connectingToAssets}
          onClickBack={onClickBack}
          onServiceSelected={onServiceSelected}
          onSkip={onSkipServiceSelection}
          progressBarSteps={progressBarSteps}
          urls={urls}
          userIsOnboarding={userIsOnboarding}
        />
      )
    case Steps.RECOMMENDED_APPLETS:
      return (
        <RecommendedApplets
          onClickBack={onClickBack}
          onSkip={onSkipRecommendedApplets}
          progressBarSteps={progressBarSteps}
          selectedService={selectedService}
          userIsOnboarding={userIsOnboarding}
        />
      )
    default:
      return null
  }
}

export default NewUserIntake
